/**
 * Card Quantity Selector
 * Custom element for product card quantity management after adding to cart
 */

if (!customElements.get('card-quantity-selector')) {
  class CardQuantitySelector extends HTMLElement {
    constructor() {
      super();
      this.quantity = 0;
      this.variantId = null;
      this.isUpdating = false;
      this.debounceTimer = null;
      this.ignoreNextEvent = false;
    }

    connectedCallback() {
      this.variantId = this.dataset.variantId;
      this.valueDisplay = this.querySelector('.card-qty-selector__value');
      this.minusBtn = this.querySelector('.card-qty-selector__btn--minus');
      this.plusBtn = this.querySelector('.card-qty-selector__btn--plus');
      this.container = this.closest('.card__quick-add-container');
      this.addToCartBtn = this.container?.querySelector('.js-card-atc-btn');

      // Bind event handlers once and store references for removal
      this.boundHandleMinus = this.handleMinus.bind(this);
      this.boundHandlePlus = this.handlePlus.bind(this);
      this.boundHandleCartUpdate = this.handleCartUpdate.bind(this);

      // Bind event listeners
      this.minusBtn?.addEventListener('click', this.boundHandleMinus);
      this.plusBtn?.addEventListener('click', this.boundHandlePlus);

      // Listen for cart updates from other sources
      document.addEventListener('on:cart:add', this.boundHandleCartUpdate);
      document.addEventListener('on:line-item:change', this.boundHandleCartUpdate);

      // Check if this product is already in cart on page load
      this.checkCartForVariant();
    }

    disconnectedCallback() {
      this.minusBtn?.removeEventListener('click', this.boundHandleMinus);
      this.plusBtn?.removeEventListener('click', this.boundHandlePlus);
      document.removeEventListener('on:cart:add', this.boundHandleCartUpdate);
      document.removeEventListener('on:line-item:change', this.boundHandleCartUpdate);
    }

    /**
     * Check cart for this variant and show quantity selector if present
     */
    async checkCartForVariant() {
      try {
        const response = await fetch(`${theme.routes.cart}.js`);
        if (!response.ok) return;

        const cart = await response.json();
        const lineItem = cart.items.find(item => item.variant_id === parseInt(this.variantId));

        if (lineItem && lineItem.quantity > 0) {
          this.quantity = lineItem.quantity;
          this.show();
        } else {
          this.hide();
        }
      } catch (error) {
        console.error('Error checking cart:', error);
      }
    }

    /**
     * Update which variant this quantity selector is tracking (e.g. when card option changes)
     * and refresh UI state to match the cart.
     * @param {string|number} nextVariantId
     */
    async setVariantId(nextVariantId) {
      const next = `${nextVariantId || ''}`.trim();
      if (!next) return;
      if (`${this.variantId}` === next) return;

      this.variantId = next;
      this.dataset.variantId = next;

      // Immediately reset UI so we don't show stale quantity from previous variant.
      this.hide();

      await this.checkCartForVariant();
    }

    /**
     * Handle cart update events
     */
    handleCartUpdate(event) {
      const { variantId, newQuantity, cart } = event.detail;

      // Check if this event is for our variant
      if (parseInt(variantId) === parseInt(this.variantId)) {
        // Ignore events that we triggered ourselves
        if (this.ignoreNextEvent) {
          this.ignoreNextEvent = false;
          return;
        }

        if (newQuantity !== undefined) {
          this.quantity = newQuantity;
          this.updateDisplay();
          if (newQuantity === 0) {
            this.hide();
          } else {
            this.show();
          }
        } else if (cart) {
          // on:cart:add doesn't include newQuantity, so find it in cart
          const lineItem = cart.items.find(item => item.variant_id === parseInt(this.variantId));
          if (lineItem) {
            this.quantity = lineItem.quantity;
            this.show();
          }
        }
      }
    }

    /**
     * Show the quantity selector and hide the add to cart button
     */
    show() {
      this.hidden = false;
      this.updateDisplay();
      this.container?.classList.add('qty-active');
    }

    /**
     * Hide the quantity selector and show the add to cart button
     */
    hide() {
      this.hidden = true;
      this.quantity = 0;
      this.container?.classList.remove('qty-active');
    }

    /**
     * Update the displayed quantity
     */
    updateDisplay() {
      if (this.valueDisplay) {
        this.valueDisplay.textContent = this.quantity;
      }
      // Disable minus button if quantity is 1
      if (this.minusBtn) {
        // We allow going to 0 to remove from cart, so don't disable
        this.minusBtn.disabled = false;
      }
    }

    /**
     * Handle minus button click
     */
    handleMinus(event) {
      event.preventDefault();
      if (this.isUpdating) return;

      const newQuantity = this.quantity - 1;
      if (newQuantity >= 0) {
        this.updateQuantity(newQuantity);
      }
    }

    /**
     * Handle plus button click
     */
    handlePlus(event) {
      event.preventDefault();
      if (this.isUpdating) return;

      const newQuantity = this.quantity + 1;
      this.updateQuantity(newQuantity);
    }

    /**
     * Update quantity in cart
     */
    async updateQuantity(newQuantity) {
      // Clear any existing debounce timer
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      // Optimistically update the UI
      const oldQuantity = this.quantity;
      this.quantity = newQuantity;
      this.updateDisplay();

      // If removing from cart
      if (newQuantity === 0) {
        this.hide();
      }

      // Debounce the actual cart update
      this.debounceTimer = setTimeout(async () => {
        await this.sendCartUpdate(newQuantity, oldQuantity);
      }, 300);
    }

    /**
     * Send the cart update request
     */
    async sendCartUpdate(newQuantity, oldQuantity) {
      this.isUpdating = true;

      try {
        const response = await fetch(`${theme.routes.cart}/change.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: this.variantId,
            quantity: newQuantity
          })
        });

        if (!response.ok) throw new Error(response.status);

        const cart = await response.json();

        // Get the actual quantity from cart response (may differ from requested if inventory limited)
        const lineItem = cart.items.find(item => item.variant_id === parseInt(this.variantId));
        const actualQuantity = lineItem ? lineItem.quantity : 0;

        // Update our quantity to match actual cart
        this.quantity = actualQuantity;
        this.updateDisplay();

        if (actualQuantity === 0) {
          this.hide();
        }

        // Update cart icon
        this.updateCartIcon();

        // Set flag to ignore our own event
        this.ignoreNextEvent = true;

        // Dispatch event for other components
        document.dispatchEvent(new CustomEvent('on:line-item:change', {
          bubbles: true,
          detail: {
            cart: cart,
            variantId: parseInt(this.variantId),
            oldQuantity: oldQuantity,
            newQuantity: actualQuantity
          }
        }));

        // Refresh cart drawer if needed
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
          bubbles: true
        }));

      } catch (error) {
        console.error('Error updating cart:', error);
        // Revert on error
        this.quantity = oldQuantity;
        this.updateDisplay();
        if (oldQuantity > 0) {
          this.show();
        }

        document.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: error.message
          }
        }));
      } finally {
        this.isUpdating = false;
      }
    }

    /**
     * Update the cart icon count
     */
    async updateCartIcon() {
      try {
        const response = await fetch(`${theme.routes.cart}?section_id=cart-icon-bubble`);
        if (!response.ok) return;

        const text = await response.text();
        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble) {
          cartIconBubble.innerHTML = text;
        }
      } catch (error) {
        console.error('Error updating cart icon:', error);
      }
    }
  }

  customElements.define('card-quantity-selector', CardQuantitySelector);
}

